import { AfterViewChecked, Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild, inject, signal } from '@angular/core';
import type { Prefilled, Prepared, SaveOutcome } from '../../services/mcp/actions/confirm-lane';
import type { ScreenActionAnswer } from '../../services/mcp/actions/fast-lane';
import { ConfirmWaitsNgService } from '../../services/mcp/actions/confirm-waits-ng.service';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { WorkspacesService, Workspace, WorkspaceUser, WorkspaceInvitation, ProjectSwitches, RoleEvent } from '../../services/workspaces.service';
import {
  Access, activePeople, ADMIN_GRANT_TITLE, ARRIVAL_NOTICE_COPY, ARRIVAL_NOTICE_LABEL, arrivalNoticeOn, adminGrantBody, ALLOW_DIRECT_ADDS_COPY, ALLOW_INVITATIONS_COPY, auditLine, Control, doorsInto,
  invitationRow, INVITE_ROLE_LATER, isErased, isLastAdmin, removedPeople, renameControl, REMOVE_PERSON_CONSEQUENCES, resendBlockedLine,
  roleChangeControl, roleHistoryEntry, RoleHistoryEntry, roleHistorySince, roleIsNoAccess, roleName, roleOptions, RoleOption, roleTrailLine, roleValue,
  seatCapControl, seatLabel, seatMeter, SeatMeter, SEARCH_WORKSPACE_NOT_CHECKED,
  SEAT_CAP_LOWERING_NOTE, seatOvershootLine, seatsAreFull, shortDate, switchDescription, workspaceMarkers,
  ADD_EMAIL_NOTE, canAddEmail, canEditDetails, emailDraftError, linkSentLine, nameDraftError,
  canRestoreWorkspace, deletionNote, deletionReadOnly, RESTORE_CONFIRM, noAdminHint,
} from '../../services/workspaces.util';
import { AddPersonComponent, AddPersonMode } from './add-person.component';
import { monogram, toneFor } from './tones';
import { generatePassword, canSetPassword } from './password.util';
import { PeopleApiService, PersonCard, PersonSummary, TransferPreview } from '../../services/people-api.service';
import { ConfirmRequest, PeopleConfirmService } from '../../services/people-confirm.service';
import { EDITOR_THEME, rowActions, reinstateRoleChoices, reinstatePrefill, entityChoices, EntityChoice, RoleChoice } from '../../services/people-actions.util';

const UNCHANGED = 'Nothing was changed.';

/** A server's sentence can quote what it was sent, and a generated password is nowhere Claude's answer scrubber can see it. */
function withoutPassword(said: string, password: string | undefined): string {
  return password ? said.split(password).join('[password]') : said;
}

/**
 * One workspace, collapsed by default. Task 6 builds the collapsed row (name, seat count, quiet
 * markers, rename-in-place) and the expanded placeholder; Tasks 7-10 fill the expanded area with
 * who is in the workspace, invitations, and its settings.
 *
 * Open/closed is NOT local state here — `open` is the tab's decision (it holds the `Set<string>`
 * of open ids so several rows can be open at once, and so a freshly created workspace can be
 * opened on arrival without this component needing to know why). This component only asks, via
 * `toggle`, to flip its own membership in that set.
 *
 * Named `open`, deliberately NOT `forceOpen`: `workspaces.util.ts`'s `filterWorkspace` already
 * returns a `forceOpen` meaning "the search wants this row expanded", which is a DIFFERENT thing
 * from "this row is open" — ORing that into a same-named input would make a search-matched row
 * impossible to collapse by hand (`toggle` removes the id from the tab's set, the OR immediately
 * puts it back). Task 11's search wiring should fold a match into the tab's `openIds` set once
 * per search-term change, not OR it in at this binding.
 */
@Component({
  selector: 'app-workspace-row',
  standalone: true,
  imports: [CommonModule, FormsModule, AddPersonComponent],
  templateUrl: './workspace-row.component.html',
  styleUrl: './workspace-row.component.css',
})
export class WorkspaceRowComponent implements AfterViewChecked, OnChanges, OnDestroy {
  @Input() workspace!: Workspace;
  @Input() access!: Access;
  @Input() open = false;
  /** "2 of 14 match" (Task 11) — the tab's own `filterWorkspace(...).countLabel`, rendered beside
   *  the name exactly as computed; this component decides nothing about what it means, only where
   *  it sits. Empty whenever no search is narrowing this workspace's people (including a workspace
   *  matched by its OWN name, which shows everybody and so carries no count — see `filterWorkspace`'s
   *  own comment in workspaces.util.ts). */
  @Input() countLabel = '';
  /** The ids `filterWorkspace` actually matched for this workspace (active + removed combined),
   *  from the tab's own `matchedUserIdsFor` — fix-round-1 #3: this row used to render everything it
   *  had lazily loaded via `users()` regardless of what `countLabel` next to the name claimed ("2 of
   *  14 match" beside a panel showing all 14). `null` means NARROW NOTHING — show everybody: an
   *  empty term, a workspace matched by its OWN name, or (fix-round-3 Critical 2) a workspace whose
   *  people the search could not load at all, which `searchFailed` below is what explains. See
   *  `matchedUserIdsFor`'s own comment on the tab for why all three come through as `null`. */
  @Input() matchedUserIds: Set<string> | null = null;
  /** This workspace is one the current search pass could not load (fix-round-3 Critical 2) — the
   *  tab's own `searchFailedFor`. It always arrives with `matchedUserIds: null`, so the people list
   *  below is already showing EVERYONE; this input is what lets the row say why, instead of leaving
   *  an unexplained unfiltered list under a "2 of 14 match" count that isn't there either. The row
   *  never re-decides it: whether a request failed is the tab's fact, not this component's. */
  @Input() searchFailed = false;
  /** The PROJECT-wide switches (`WorkspacesTabComponent.switches`) — carried down here (Task 9)
   *  purely to hand to `app-add-person` and to this row's own invitations empty state, neither of
   *  which can otherwise tell a project-level "off" from this one workspace's own. Null only before
   *  the tab's first load resolves; both consumers treat that as "not disabled" rather than blocking
   *  on a still-loading fact. */
  @Input() switches: ProjectSwitches | null = null;
  /** Whether Developer mode is on (fix-round-1 finding 4) — carried down alongside `switches` for
   *  the same reason: `app-add-person` and this row's own invitations empty state both need it to
   *  read `doorsInto`'s builders'-workspace case correctly. See that Input's own comment on
   *  `AddPersonComponent` for why a missing value reads as OFF rather than "unknown". */
  @Input() developerModeOn = false;
  /** Role id → name, loaded once by the tab (`WorkspacesTabComponent.roleNames`). Only ever used to
   *  LABEL a pill (`roleName`, workspaces.util.ts) — null or missing an id simply shows the id. */
  @Input() roleNames: ReadonlyMap<string, string> | null = null;
  @Output() changed = new EventEmitter<void>();
  @Output() toggle = new EventEmitter<void>();

  @ViewChild('nameInput') private nameInputRef?: ElementRef<HTMLInputElement>;
  @ViewChild(AddPersonComponent) private addForm?: AddPersonComponent;
  private focusPending = false;

  private ws = inject(WorkspacesService);
  private people = inject(PeopleApiService);
  private peopleConfirm = inject(PeopleConfirmService);
  private waits = inject(ConfirmWaitsNgService);
  readonly isErased = isErased;
  /** Which row's ⋯ menu is open, if any. */
  menuFor = signal<string | null>(null);
  /** A refusal that came before any overlay opened (the person's summary could not be read), shown under that row. */
  actionError = signal<{ id: string; text: string } | null>(null);

  actions(u: WorkspaceUser) { return rowActions(u); }

  // ─── A workspace being deleted: read-only, and restorable by a builder while it is pending. ───
  readonly RESTORE_CONFIRM = RESTORE_CONFIRM;
  deletionNote(): ReturnType<typeof deletionNote> { return deletionNote(this.workspace); }
  /** Every change control on the row is hidden or disabled while this is true; the history stays readable. */
  readOnly(): boolean { return !!deletionReadOnly(this.workspace); }
  canRestore(): boolean { return canRestoreWorkspace(this.workspace, this.access); }
  /** Counts admins only from a loaded people list; a stale one is dropped (`usersLoaded`) when membership moves. */
  noAdminHint(): string | null { return noAdminHint(this.workspace, this.usersLoaded ? this.users() : null); }
  confirmingRestore = signal(false);
  restoring = signal(false);
  restoreError = signal('');

  /** The header owns the expand toggle, so every click in the restore controls stops there. */
  askRestore(ev: Event): void {
    ev.stopPropagation();
    this.restoreError.set('');
    this.confirmingRestore.set(true);
  }
  cancelRestore(ev: Event): void {
    ev.stopPropagation();
    if (this.restoring()) return;
    this.confirmingRestore.set(false);
    this.heard('restore-workspace', { outcome: 'discarded' });
  }
  /**
   * Any refusal is the server's own sentence, whatever its code, shown under the name, so a refusal added on the
   * server needs nothing here. The page reloads either way, so the row shows where the workspace really stands now.
   */
  async confirmRestore(ev: Event): Promise<void> {
    ev.stopPropagation();
    if (this.restoring() || !this.canRestore()) return;
    this.restoring.set(true);
    this.restoreError.set('');
    this.heardSaving('restore-workspace');
    const a = await this.ws.restoreWorkspace(this.workspace.id);
    this.restoring.set(false);
    this.confirmingRestore.set(false);
    if (!a.ok) this.restoreError.set(a.error);
    // The callout closes either way, so a refusal ends the wait: there is nothing left on screen to press again.
    this.heard('restore-workspace', a.ok ? { outcome: 'saved', saved: { workspaceId: this.workspace.id } } : { outcome: 'closed', because: a.error });
    this.changed.emit();
  }
  toggleMenu(u: WorkspaceUser): void { this.menuFor.set(this.menuFor() === u.id ? null : u.id); }

  /** The summary every people overlay starts from; on failure the reason goes under the row. */
  private async summaryFor(u: WorkspaceUser) {
    this.menuFor.set(null);
    this.actionError.set(null);
    const s = await this.people.summary(u.id);
    if (!s.ok || !s.data.person) {
      this.actionError.set({ id: u.id, text: s.ok ? 'That person is not in this app.' : s.error });
      return null;
    }
    return s.data;
  }

  async openErase(u: WorkspaceUser): Promise<void> {
    const s = await this.summaryFor(u);
    if (!s) return;
    await this.askErase(u, s);
  }

  /** The overlay's service is shared by every row, so only the row that asked may report it as unsaved. */
  private peopleAsked = signal<ConfirmRequest['kind'] | null>(null);
  private async askPeople(...args: Parameters<PeopleConfirmService['ask']>): ReturnType<PeopleConfirmService['ask']> {
    this.peopleAsked.set(args[0].kind);
    try {
      return await this.peopleConfirm.ask(...args);
    } finally {
      this.peopleAsked.set(null);
    }
  }

  private async askErase(u: WorkspaceUser, s: PersonSummary, typed?: string): Promise<void> {
    const out = await this.askPeople(
      { kind: 'erase', person: s.person!, targets: s.targets, total: s.total, theme: EDITOR_THEME, ...(typed ? { typed } : {}) },
      async () => {
        this.heardSaving('erase-person');
        const r = await this.people.erase(u.id);
        if (!r.ok) this.heard('erase-person', { outcome: 'failed', reason: r.error });
        return r.ok ? { ok: true } : { ok: false, error: r.error };
      });
    this.heard('erase-person', out.outcome === 'success' ? { outcome: 'saved', saved: { workspaceId: this.workspace.id, personId: u.id } } : { outcome: 'discarded' });
    if (out.outcome !== 'success') return;
    // An erased person stays in Removed, so neither count moves and ngOnChanges sees no membership
    // change — without this the row kept showing their name and a Reinstate button until a reload.
    this.loadUsers(true);
    this.changed.emit();
  }

  async openReinstate(u: WorkspaceUser): Promise<void> {
    const s = await this.summaryFor(u);
    if (!s) return;
    const person = s.person!;
    await this.askReinstate(u, s, this.reinstateChoices(person), undefined, person.signsInByLink ? '' : generatePassword());
  }

  private reinstateChoices(person: PersonCard): RoleChoice[] {
    return reinstateRoleChoices([...(this.roleNames ?? new Map<string, string>())].map(([id, name]) => ({ id, name })), person);
  }

  private async askReinstate(u: WorkspaceUser, s: PersonSummary, choices: RoleChoice[], roleId: string | undefined, password: string): Promise<void> {
    const person = s.person!;
    let chosenRole = '';
    const out = await this.askPeople({
      kind: 'reinstate', person, targets: s.targets, total: s.total, theme: EDITOR_THEME,
      roles: choices, roleId: roleId ?? reinstatePrefill(choices, person),
      needsPassword: !person.signsInByLink, password,
    }, async (c) => {
      this.heardSaving('reinstate-person');
      chosenRole = c.roleId || '';
      const r = await this.people.reinstate(u.id, c.roleId || '', c.password);
      if (!r.ok) this.heard('reinstate-person', { outcome: 'failed', reason: withoutPassword(r.error, c.password) });
      return r.ok ? { ok: true, notice: r.data.warning } : { ok: false, error: r.error };
    });
    this.heard('reinstate-person', out.outcome === 'success'
      ? { outcome: 'saved', saved: { workspaceId: this.workspace.id, personId: u.id, roleId: chosenRole, ...(out.notice ? { notice: out.notice } : {}) } }
      : { outcome: 'discarded' });
    if (out.outcome !== 'success') return;
    if (out.notice) this.actionError.set({ id: u.id, text: out.notice });
    this.changed.emit();
  }

  transferFrom = signal<WorkspaceUser | null>(null);
  transferTo = signal('');
  transferChoices = signal<EntityChoice[]>([]);
  transferPicked = signal<ReadonlySet<string>>(new Set());
  transferError = signal('');
  transferBusy = signal(false);

  /** Who can receive the work: anyone still in this workspace except the giver (spec: the receiver must be active). */
  receivers(): WorkspaceUser[] {
    const from = this.transferFrom();
    return this.users().filter((p) => !p.removedAt && p.id !== from?.id);
  }

  async openTransfer(u: WorkspaceUser): Promise<void> {
    const s = await this.summaryFor(u);
    if (!s) return;
    const choices = entityChoices(s.targets);
    this.transferChoices.set(choices);
    this.transferPicked.set(new Set(choices.map((c) => c.id))); // all ticked by default (owner, 2026-10-03)
    this.transferTo.set('');
    this.transferError.set('');
    this.transferFrom.set(u);
  }

  togglePicked(id: string): void {
    const next = new Set(this.transferPicked());
    next.has(id) ? next.delete(id) : next.add(id);
    this.transferPicked.set(next);
  }

  closeTransfer(): void { if (!this.transferBusy()) this.transferFrom.set(null); }

  async previewTransfer(): Promise<void> {
    const from = this.transferFrom();
    const to = this.transferTo();
    const picked = [...this.transferPicked()];
    if (!from || !to || !picked.length || this.transferBusy()) return;
    this.transferBusy.set(true);
    this.transferError.set('');
    const p = await this.people.previewTransfer(from.id, to, picked);
    this.transferBusy.set(false);
    if (!p.ok || !p.data.from || !p.data.to) { this.transferError.set(p.ok ? 'One of those people is not in this app.' : p.error); return; }
    this.transferFrom.set(null);
    await this.askTransfer(from.id, to, picked, p.data);
  }

  private async askTransfer(from: string, to: string, picked: string[], preview: TransferPreview): Promise<void> {
    const out = await this.askPeople(
      { kind: 'transfer', from: preview.from!, to: preview.to!, targets: preview.targets, total: preview.total, theme: EDITOR_THEME },
      async () => {
        this.heardSaving('transfer-person');
        const r = await this.people.transfer(from, to, picked);
        if (!r.ok) this.heard('transfer-person', { outcome: 'failed', reason: r.error });
        return r.ok ? { ok: true } : { ok: false, error: r.error };
      });
    this.heard('transfer-person', out.outcome === 'success' ? { outcome: 'saved', saved: { workspaceId: this.workspace.id, personId: from, toPersonId: to, entityIds: picked } } : { outcome: 'discarded' });
    if (out.outcome === 'success') this.changed.emit();
  }

  /** Exposed as instance fields (not re-wrapped) so the template calls the same functions Task 2
   *  specced — see `isProtectedPage = isProtectedPage;` in pages.component.ts for the precedent. */
  seatLabel = seatLabel;
  workspaceMarkers = workspaceMarkers;
  activePeople = activePeople;
  removedPeople = removedPeople;
  invitationRow = invitationRow;
  seatsAreFull = seatsAreFull;
  seatCapControl = seatCapControl;
  auditLine = auditLine;
  switchDescription = switchDescription;
  seatOvershootLine = seatOvershootLine;
  ALLOW_INVITATIONS_COPY = ALLOW_INVITATIONS_COPY;
  ALLOW_DIRECT_ADDS_COPY = ALLOW_DIRECT_ADDS_COPY;
  ARRIVAL_NOTICE_LABEL = ARRIVAL_NOTICE_LABEL;
  ARRIVAL_NOTICE_COPY = ARRIVAL_NOTICE_COPY;
  arrivalNoticeOn = arrivalNoticeOn;
  SEAT_CAP_LOWERING_NOTE = SEAT_CAP_LOWERING_NOTE;
  SEARCH_WORKSPACE_NOT_CHECKED = SEARCH_WORKSPACE_NOT_CHECKED;
  renameControl = renameControl;
  resendBlockedLine = resendBlockedLine;
  REMOVE_PERSON_CONSEQUENCES = REMOVE_PERSON_CONSEQUENCES;
  ADMIN_GRANT_TITLE = ADMIN_GRANT_TITLE;
  ADD_EMAIL_NOTE = ADD_EMAIL_NOTE;
  canEditDetails = canEditDetails;
  canAddEmail = canAddEmail;
  adminGrantBody = adminGrantBody;
  INVITE_ROLE_LATER = INVITE_ROLE_LATER;
  roleTrailLine = roleTrailLine;
  roleHistorySince = roleHistorySince;

  /** One person's history open at a time, loaded on demand — the same lazy, newest-wins discipline as
   *  `loadUsers` (`historySeq` drops a response that a newer request has already overtaken). */
  historyFor = signal('');
  history = signal<{ startedAt: string | null; events: RoleEvent[] } | null>(null);
  historyLoading = signal(false);
  historyError = signal('');
  private historySeq = 0;

  async toggleHistory(u: WorkspaceUser): Promise<void> {
    if (this.historyFor() === u.id) { this.historyFor.set(''); this.history.set(null); return; }
    this.historyFor.set(u.id);
    this.history.set(null);
    await this.loadHistory(u);
  }

  async loadHistory(u: WorkspaceUser): Promise<void> {
    const seq = ++this.historySeq;
    this.historyLoading.set(true);
    this.historyError.set('');
    const a = await this.ws.roleHistory(this.workspace.id, u.id);
    if (seq !== this.historySeq) return;
    this.historyLoading.set(false);
    if (!a.ok) { this.historyError.set(a.error); return; }
    this.history.set(a.data);
  }

  historyEntries(): RoleHistoryEntry[] {
    return (this.history()?.events ?? []).map((e) => roleHistoryEntry(e, this.roleNames));
  }

  editingName = signal(false);
  nameDraft = '';
  renaming = signal(false);
  renameError = signal('');

  /**
   * Who is in this workspace, loaded lazily on first expand — never fetched for a row nobody has
   * opened. `usersLoading` drives the three-skeleton-rows state (the FIRST load only); `refreshingUsers`
   * covers every load after that (a reload triggered by `ngOnChanges` below) so the list stays mounted
   * instead of flashing back to a skeleton, mirroring the tab's own `loading`/`refreshing` split.
   */
  users = signal<WorkspaceUser[]>([]);
  usersLoading = signal(false);
  refreshingUsers = signal(false);
  usersError = signal('');
  /** Not private: the template gates the whole body on this (`*ngIf="usersLoaded"`) so a failed
   *  refresh never blanks an already-loaded list — only the very first, never-yet-successful load
   *  shows nothing but the error. */
  usersLoaded = false;
  /** Bumped on every `loadUsers()` call; a response only applies if it is still the newest one in
   *  flight. Without this, expanding a row while an earlier load for the same row is still pending
   *  (e.g. a rename lands mid-request) lets the two resolve out of order, and whichever happens to
   *  finish last wins even if it started first — leaving a stale snapshot on screen. */
  private loadSeq = 0;

  showRemoved = signal(false);

  /** The one person whose role is being saved (by id) — every picker on the row quiesces meanwhile,
   *  the same whole-list discipline as `busyInvite`. `roleError` is that person's refusal, word for
   *  word from the server (the last admin, another builder's role) — shown under their name. */
  savingRole = signal('');
  roleError = signal<{ id: string; message: string } | null>(null);
  /** A grant of admin waiting on its confirmation (owner's rule: granting admin always asks). The
   *  select is kept so Cancel can put its value back — it has already changed in the DOM. */
  pendingAdmin = signal<{ user: WorkspaceUser; sel: HTMLSelectElement } | null>(null);

  removeTarget = signal<WorkspaceUser | null>(null);
  removing = signal(false);
  removeError = signal('');

  // Set password (Admin sets password). The password is shown ONCE: closing the modal forgets it.
  pwTarget = signal<WorkspaceUser | null>(null);
  pwValue = signal('');
  pwSaving = signal(false);
  pwError = signal('');
  pwDone = signal(false);
  pwCopied = signal(false);
  readonly canSetPassword = canSetPassword;

  /** ws 9b-3 W11 — one small modal for Edit name / Add email. */
  detailTarget = signal<WorkspaceUser | null>(null);
  detailMode = signal<'name' | 'email'>('name');
  detailDraft = '';
  detailSaving = signal(false);
  detailError = signal('');
  detailDone = signal('');

  openEditName(u: WorkspaceUser): void { this.menuFor.set(null); this.detailMode.set('name'); this.detailDraft = u.userName || ''; this.openDetail(u); }
  openAddEmail(u: WorkspaceUser): void { this.menuFor.set(null); this.detailMode.set('email'); this.detailDraft = ''; this.openDetail(u); }
  private openDetail(u: WorkspaceUser): void { this.detailTarget.set(u); this.detailError.set(''); this.detailDone.set(''); }
  closeDetail(): void {
    if (this.detailSaving()) return;
    const changed = !!this.detailDone();
    this.detailTarget.set(null); this.detailDone.set('');
    this.heard('set-person-sign-in-email', { outcome: 'discarded' });
    if (changed) this.changed.emit();
  }
  async saveDetail(): Promise<void> {
    const t = this.detailTarget();
    if (!t || this.detailSaving()) return;
    const isName = this.detailMode() === 'name';
    const err = isName ? nameDraftError(this.detailDraft) : emailDraftError(this.detailDraft);
    if (err) { this.detailError.set(err); return; }
    this.detailSaving.set(true); this.detailError.set('');
    if (isName) {
      const n = await this.ws.updatePersonName(this.workspace.id, t.id, this.detailDraft);
      this.detailSaving.set(false);
      if (!n.ok) { this.detailError.set(n.error); return; }
      this.detailDone.set('saved'); this.closeDetail(); return;
    }
    this.heardSaving('set-person-sign-in-email');
    const a = await this.ws.addPersonEmail(this.workspace.id, t.id, this.detailDraft);
    this.detailSaving.set(false);
    if (!a.ok) { this.detailError.set(a.error); this.heard('set-person-sign-in-email', { outcome: 'failed', reason: a.error }); return; }
    // Was (2026-10-07, ws 9b-3 final review): const notice = (a.data as any)?.notice; if (notice) {…return;} then linkSentLine.
    // "Link sent" only when it WAS sent; anything else says so and the modal stays open.
    const d = a.data;
    if (d?.emailOutcome !== 'sent') {
      this.detailError.set(d?.notice || "The email couldn't be sent. Try adding it again.");
      this.heard('set-person-sign-in-email', { outcome: 'failed', reason: this.detailError() });
      return;
    }
    this.detailDone.set(linkSentLine(this.detailDraft));
    this.heard('set-person-sign-in-email', { outcome: 'saved', saved: { workspaceId: this.workspace.id, personId: t.id, email: this.detailDraft.trim() } });
  }

  /**
   * The settings strip (Task 10): seat cap + the two doors, rendered above the add-person form. Not
   * lazily loaded like `users`/`invitations` above — the fields it needs (`seatLimit`, `invitesEnabled`,
   * `addsEnabled`, `settingsChangedBy/At`) already arrive on the `Workspace` object the tab's `list`
   * call returns, so there is nothing separate to fetch here.
   *
   * `seatCapDraft`/`seatCapUnlimited` mirror `nameDraft`'s pattern: a plain field, not a signal, kept
   * in sync with `workspace.seatLimit` from `ngOnChanges` — but only while `seatCapDirty` is false, so
   * an unrelated silent reload (someone else's action elsewhere on the tab) can never overwrite a
   * value the builder is mid-typing and has not yet saved. `seatCapDirty` clears once a save succeeds,
   * at which point the freshly-saved value (not the reload, which may not have landed yet) is already
   * what the draft holds, so there is nothing to resync.
   */
  seatCapDraft: number | null = null;
  seatCapUnlimited = false;
  private seatCapDirty = false;
  savingSeatCap = signal(false);
  seatCapError = signal('');
  /** What THIS save just did — set only when `setSeatLimit`'s own response says the workspace now
   *  sits over its new cap, so lowering it never happens silently. Cleared on the next edit/save. */
  seatCapOvershoot = signal<{ activeCount: number; limit: number } | null>(null);

  /** Which of the two workspace switches is mid-flight, so only ITS control shows "Saving…" — same
   *  shape as `WorkspacesTabComponent.busy` for the project-wide switches. */
  // Was (2026-10-07, ws 9b-2): savingSwitch = signal<'' | 'invites' | 'adds'>('');
  savingSwitch = signal<'' | 'invites' | 'adds' | 'arrivals'>('');
  switchError = signal('');

  /**
   * Invitations — the same lazy-load discipline as `users` above (first-expand fetch, `membershipChanged`
   * invalidation from `ngOnChanges`), kept as its own signals and its own sequence token because an
   * invitation reload (resend/cancel, Step 4) must not disturb the people list or vice versa, and a
   * failed people load must not blank a working invitations list or the other way round.
   */
  invitations = signal<WorkspaceInvitation[]>([]);
  invitesLoading = signal(false);
  refreshingInvites = signal(false);
  invitesError = signal('');
  invitesLoaded = false;
  private loadInvitesSeq = 0;

  showInviteHistory = signal(false);

  /** Only one resend/cancel in flight per row at a time, tagged with WHICH action so the two buttons
   *  never both read "Sending…"/"Cancelling…" for the same in-flight call. `inviteNotice` is the
   *  server's `notice` from a resend that was written but deliberately not mailed (shown on the row
   *  INSTEAD of a success message, per Step 4) — never cleared by an unrelated reload, only by
   *  starting a new action on that same row. */
  busyInvite = signal<{ id: string; action: 'resend' | 'cancel' } | null>(null);
  inviteNotice = signal<{ id: string; message: string } | null>(null);
  inviteActionError = signal<{ id: string; message: string } | null>(null);

  ngAfterViewChecked(): void {
    if (this.focusPending && this.nameInputRef) {
      this.focusPending = false;
      this.nameInputRef.nativeElement.focus();
      this.nameInputRef.nativeElement.select();
    }
  }

  /**
   * "Lazily, on first expand, and again after any change" (task 7 brief, step 1) — but a removal
   * must NOT reload from here directly (that is `confirmRemove`'s job to avoid: it only emits
   * `changed`). Instead, the tab's `(changed)="reload({ silent: true })"` replaces `workspace` with
   * a fresh object of the same id (same `trackBy`, so this component instance survives).
   *
   * "Any change" is narrowed to a MEMBERSHIP change — `activeCount`/`removedCount` actually
   * differing — rather than every `workspace` reference swap. `reload()` replaces every workspace
   * object at once, so keying on identity alone means one removal, rename or create fires
   * `listUsers` once per OPEN row (and Task 11 force-opens every search match), fanning out exactly
   * when a builder has the most rows open. A rename changes the object but not who is in it, so it
   * must not cost this row a request.
   */
  ngOnChanges(changes: SimpleChanges): void {
    // Collapsing hides what Claude filled in, so there is nothing left for the builder to press.
    if (changes['open'] && !this.open) this.heard(this.pressing() ?? '', { outcome: 'discarded' });
    const wsChange = changes['workspace'];
    const membershipChanged = !!wsChange && !wsChange.firstChange &&
      (wsChange.previousValue?.activeCount !== wsChange.currentValue?.activeCount ||
       wsChange.previousValue?.removedCount !== wsChange.currentValue?.removedCount);

    // The seat cap draft tracks the server's own `seatLimit` — except while `seatCapDirty` (an edit
    // in progress that has not yet been saved), so an unrelated reload elsewhere on the tab can never
    // clobber input the builder has not submitted. Runs whether or not the row is open: cheap, and it
    // means the strip is never stale the moment the row IS expanded.
    if (wsChange && !this.seatCapDirty) {
      this.seatCapUnlimited = this.workspace.seatLimit === null || this.workspace.seatLimit === undefined;
      this.seatCapDraft = this.seatCapUnlimited ? null : this.workspace.seatLimit;
    }

    // The overshoot notice (fix-round-1 finding 5) is set once, from the response to THIS row's own
    // save, and otherwise sits in a signal a collapse/re-expand cannot touch — so left alone it would
    // go on describing a limit and a headcount that used to be true long after either has moved,
    // including after somebody ELSE raises the cap. Cleared the moment the workspace's own numbers
    // stop matching what the notice is about; while they still match (the ordinary case — this row's
    // own `changed.emit()` brings the SAME numbers back down moments later) it is left alone rather
    // than blanked before anyone had a chance to read it.
    if (wsChange) {
      const o = this.seatCapOvershoot();
      if (o && (o.activeCount !== this.workspace.activeCount || o.limit !== this.workspace.seatLimit)) {
        this.seatCapOvershoot.set(null);
      }
    }

    if (!this.open) {
      // A closed row drops the update on the floor rather than fetching for nobody — but it must
      // not go stale forever. Clearing usersLoaded (not fetching) means the NEXT expand re-fetches
      // instead of short-circuiting on a snapshot from before the change.
      //
      // Bumping loadSeq and clearing both loading flags here too: an in-flight load for this row
      // must not be allowed to resurrect a snapshot taken before the change. Without this, a load
      // already in flight when the row collapses can still resolve AFTER this branch runs, pass the
      // (unchanged) sequence check, and set usersLoaded back to true with pre-change data — undoing
      // the invalidation. Bumping loadSeq here makes that response stale too, so it gets dropped.
      // And if the row re-expands before that abandoned load resolves, the new load only ever
      // clears the ONE flag it itself set (usersLoading or refreshingUsers) — the abandoned call's
      // flag would otherwise never be cleared by anyone and "Refreshing…" would stick on screen
      // until the component is destroyed. Clearing both here removes that ambiguity up front.
      //
      // Unreachable today (no Task 7 mutation touches a closed row's membership from outside it)
      // but Task 8 makes it reachable the moment an invitation acceptance changes a collapsed
      // workspace.
      if (membershipChanged) {
        this.usersLoaded = false;
        this.loadSeq++;
        this.usersLoading.set(false);
        this.refreshingUsers.set(false);
        // Same invalidation, same reason, for the OTHER lazy list this row owns: an invitation
        // acceptance or removal-triggered fan-back changes both activeCount and an invitation's
        // status in the same beat, so a closed row must drop a stale invitations snapshot too.
        this.invitesLoaded = false;
        this.loadInvitesSeq++;
        this.invitesLoading.set(false);
        this.refreshingInvites.set(false);
      }
      return;
    }
    // `membershipChanged` is passed through as `force`: it means the DATA moved, which must always
    // get a request out and a fresh `loadSeq`/`loadInvitesSeq`, even onto a load already in flight
    // (fix-round-2 — see the guard's own comment in each loader for why `force` exists at all).
    if (!this.usersLoaded || membershipChanged) {
      this.loadUsers(membershipChanged);
    }
    if (!this.invitesLoaded || membershipChanged) {
      this.loadInvitations(membershipChanged);
    }
  }

  /**
   * `force` (fix-round-2): the in-flight guard below and `loadSeq` do DIFFERENT jobs, and folding
   * them together (round 1's mistake) silently dropped a genuine membership change that arrived
   * while a load was already outstanding — no second request went out, so the older in-flight
   * response landed and the row sat on a stale headcount until some LATER change happened to trigger
   * another load. `force` is how a caller tells this method which situation it is:
   *   - `force` false (a plain re-entry: a search pass rewriting `countLabel`/`matchedUserIds`/
   *     `searchFailed` on this row, or a second `!usersLoaded` check while the first load is still
   *     outstanding) → the trigger carries NO new information about who is in this workspace, so the
   *     guard suppresses it. This is the case the guard exists for.
   *   - `force` true (`membershipChanged`) → the data genuinely moved, so this bypasses the guard,
   *     bumps `loadSeq` regardless of what is in flight, and fires. The OLDER in-flight call, when it
   *     resolves, sees `seq !== this.loadSeq` (the check further down) and is dropped — exactly the
   *     "newest result wins" guarantee `loadSeq` was built for, restored for this case.
   * A row collapsing mid-load is a third case and does not go through `force` at all: the closed-row
   * branch above bumps `loadSeq` and clears both loading flags directly, without calling `loadUsers`,
   * so the abandoned in-flight promise drops itself on the stale-seq check and the flags are already
   * false for the next real expand — nothing is left blocked.
   */
  async loadUsers(force = false): Promise<void> {
    // In-flight guard (fix-round-1 finding 3): `ngOnChanges` fires on ANY input change, and a
    // search pass rewrites THREE of this row's inputs at once — `countLabel`, `matchedUserIds`
    // (a fresh Set object every pass, so never reference-equal) and `searchFailed` — once per
    // keystroke, for every visible row. While a first load is still outstanding `!usersLoaded`
    // stays true, so without this a fast typist could queue one `listUsers` call per keystroke per
    // open row. (The guard's original justification named the row's own `search` Input, which
    // fix-round-3 Minor 4 removed as dead — the guard is not: the inputs above are what actually
    // change on every keystroke, and they always did.) Suppressed ONLY when `!force` — see the
    // comment above for why a membership change must never be suppressed here.
    if (!force && (this.usersLoading() || this.refreshingUsers())) return;
    const seq = ++this.loadSeq;
    const first = !this.usersLoaded;
    (first ? this.usersLoading : this.refreshingUsers).set(true);
    const a = await this.ws.listUsers(this.workspace.id);
    // A newer call started while this one was in flight (e.g. expand raced a rename's silent
    // reload) — drop this stale response so an out-of-order resolution can never overwrite what the
    // newer call already applied.
    if (seq !== this.loadSeq) return;
    (first ? this.usersLoading : this.refreshingUsers).set(false);
    if (!a.ok) { this.usersError.set(a.error); return; }
    this.usersError.set('');
    this.usersLoaded = true;
    this.users.set(a.data.users);
  }

  /** Same shape as `loadUsers` above, own sequence token (`loadInvitesSeq`), own `force` (fix-round-2:
   *  see `loadUsers`'s comment — the reasoning is identical, just for the invitations list). Also the
   *  reload target for Step 4's resend/cancel — they call this directly (with `force` left `false`;
   *  nothing else could be in flight at that moment) rather than emitting `changed`, because neither
   *  action moves a seat: `changed` would trigger the tab's full `reload()`, which is more than an
   *  invitation-only mutation needs. */
  async loadInvitations(force = false): Promise<void> {
    // Same in-flight guard as `loadUsers`, same `force` escape hatch, same reason (fix-round-1
    // finding 3, fix-round-2 correction).
    if (!force && (this.invitesLoading() || this.refreshingInvites())) return;
    const seq = ++this.loadInvitesSeq;
    const first = !this.invitesLoaded;
    (first ? this.invitesLoading : this.refreshingInvites).set(true);
    const a = await this.ws.listInvitations(this.workspace.id);
    if (seq !== this.loadInvitesSeq) return;
    (first ? this.invitesLoading : this.refreshingInvites).set(false);
    if (!a.ok) { this.invitesError.set(a.error); return; }
    this.invitesError.set('');
    this.invitesLoaded = true;
    this.invitations.set(a.data.invitations);
  }

  /** Pending-but-not-full-workspace invitations (`invitationRow(...).inHistory === false`) — shown by
   *  default under the people, per Step 2. Never re-decides `inHistory`; only filters on it. */
  pendingInvitations(): WorkspaceInvitation[] {
    const seatsFull = this.seatsAreFull(this.workspace);
    return this.invitations().filter((i) => !this.invitationRow(i, { seatsFull }).inHistory);
  }

  /** Everything `invitationRow` puts behind the "Show history" toggle: accepted, void, removed, and
   *  cancelled invitations, plus `linked_elsewhere` ones (which are not "pending" for this split even
   *  though the server's own status stays live — see `invitationRow`'s comment). */
  historyInvitations(): WorkspaceInvitation[] {
    const seatsFull = this.seatsAreFull(this.workspace);
    return this.invitations().filter((i) => this.invitationRow(i, { seatsFull }).inHistory);
  }

  toggleInviteHistory(): void {
    this.showInviteHistory.set(!this.showInviteHistory());
  }

  /** "Invited by X, 14 Sep 2026" — `shortDate` is the shared format (workspaces.util.ts); `invitedBy`
   *  is whatever the server stored for the inviter (their email, per `invite-user.function.ts`), not
   *  a lookup this component owns. */
  invitedLine(i: WorkspaceInvitation): string {
    const when = shortDate(i.createdAt);
    return when ? `Invited by ${i.invitedBy}, ${when}` : `Invited by ${i.invitedBy}`;
  }

  inviteNoticeFor(i: WorkspaceInvitation): string {
    const n = this.inviteNotice();
    return n && n.id === i.id ? n.message : '';
  }

  inviteErrorFor(i: WorkspaceInvitation): string {
    const e = this.inviteActionError();
    return e && e.id === i.id ? e.message : '';
  }

  /** Whether THIS SPECIFIC action is the one running for this row (drives which button's label
   *  switches to "Sending…"/"Cancelling…"). `[disabled]` in the template is `!!busyInvite()` — the
   *  WHOLE list quiesces during any one call, matching the guard at the top of `resendInvite` and
   *  `cancelInvite` below exactly, so a button that reads enabled is never a button that silently
   *  no-ops (fix-round-1 finding 1: a per-id `[disabled]` next to a per-component guard let every
   *  OTHER row's buttons look clickable while doing nothing). */
  isInviteBusyWith(i: WorkspaceInvitation, action: 'resend' | 'cancel'): boolean {
    const b = this.busyInvite();
    return !!b && b.id === i.id && b.action === action;
  }

  /**
   * WHETHER RESEND CAN POSSIBLY WORK (fix-round-3 Important 3). Resend is `inviteUser` again, so the
   * server applies `wayIn({ path: 'invite' })` to it — the project switch OR this workspace's own
   * `invitesEnabled` — and `resolveCallerTarget` refuses the builders' workspace with Developer mode
   * off before even that. `invitationRow(...).actions` knows none of it: it says "pending, therefore
   * resendable", which is a fact about the invitation, not about the door.
   *
   * So a builder who switched THIS workspace's invitations off read "Invitations are switched off for
   * this workspace." in the settings strip, watched the add-person form hide its email mode, pressed
   * an enabled Resend on the invitation below, and got the server's message for a DIFFERENT switch:
   * "Invitations are switched off for this app." Two sentences about one switch, on one screen.
   *
   * `doors().invite` is the same verdict the strip and the form already render, so all three now say
   * the same thing — and the disabled control carries that verdict's own `reason` rather than waiting
   * for a 403 written for another situation. Same shape as `usernameSeatControl` in
   * `add-person.component.ts`: pre-empt a certain failure instead of letting it land as a refusal.
   *
   * Cancel is deliberately NOT gated: it goes through `cancelInvite`, which no door closes — calling
   * off an invitation is always allowed, and is very often exactly what a builder wants to do with
   * the pending invitations left behind after switching invitations off.
   */
  resendControl(): Control {
    return this.doors().invite;
  }

  /**
   * Resend IS `inviteUser` again (Task 1's comment on the service method) — same address, same role.
   * A `notice` in the answer means the server wrote the invitation but deliberately did not mail it
   * (e.g. it was born `linked_elsewhere`); that replaces any success message, per Step 4 — there is
   * nothing else to tell the builder when the call succeeds without a notice, so nothing is shown.
   */
  async resendInvite(i: WorkspaceInvitation): Promise<SaveOutcome> {
    if (this.busyInvite()) return { outcome: 'failed', reason: 'Another invitation on this workspace is still being sent.' };
    const door = this.resendControl();
    if (!door.enabled) return { outcome: 'failed', reason: door.reason };
    this.busyInvite.set({ id: i.id, action: 'resend' });
    this.inviteActionError.set(null);
    this.inviteNotice.set(null);
    const a = await this.ws.resendInvitation(this.workspace.id, i.email, i.roleId);
    this.busyInvite.set(null);
    if (!a.ok) { this.inviteActionError.set({ id: i.id, message: a.error }); return { outcome: 'failed', reason: a.error }; }
    if (a.data.notice) this.inviteNotice.set({ id: i.id, message: a.data.notice });
    await this.loadInvitations();
    return { outcome: 'saved', saved: { inviteId: i.id, email: i.email, ...(a.data.notice ? { notice: a.data.notice } : {}) } };
  }

  /**
   * ALWAYS the invitation's id, never the address — Task 12 tightens `cancelInvite` so an
   * address-only cancel must name a workspace and can no longer also void an unrelated pending
   * `attach_email` invitation to the same person. This is the first caller with an id in hand.
   */
  async cancelInvite(i: WorkspaceInvitation): Promise<SaveOutcome> {
    if (this.busyInvite()) return { outcome: 'failed', reason: 'Another invitation on this workspace is still being sent.' };
    this.busyInvite.set({ id: i.id, action: 'cancel' });
    this.inviteActionError.set(null);
    this.inviteNotice.set(null);
    const a = await this.ws.cancelInvitation(this.workspace.id, i.id);
    this.busyInvite.set(null);
    if (!a.ok) { this.inviteActionError.set({ id: i.id, message: a.error }); return { outcome: 'failed', reason: a.error }; }
    await this.loadInvitations();
    return { outcome: 'saved', saved: { inviteId: i.id, email: i.email } };
  }

  /**
   * Fix-round-1 #3: what actually renders in the people list — `activePeople(users())`, narrowed to
   * `matchedUserIds` when a search set it. `null` (no active search, or a workspace matched by its
   * own name) means show everyone, exactly as before this fix. Deliberately NOT used for the "N
   * people hold seats" count or the "Nobody holds a seat" empty state below — those are facts about
   * total occupancy and must stay true regardless of what a search happens to be narrowing the
   * VIEW to.
   */
  visibleActivePeople(): WorkspaceUser[] {
    const all = this.activePeople(this.users());
    return this.matchedUserIds ? all.filter((u) => this.matchedUserIds!.has(u.id)) : all;
  }

  /** Same narrowing as `visibleActivePeople()`, for the removed list — `filterWorkspace`'s own
   *  comment (workspaces.util.ts) requires both lists narrow together, since a removed person is
   *  still a person a search can find. Unlike the active list, narrowing here also drives the
   *  "Removed (n)" toggle's own count (see the template): showing "Removed (3)" while a search has
   *  a specific removed person in mind, with none of the 3 actually matching, would say the wrong
   *  thing about what pressing it reveals. */
  visibleRemovedPeople(): WorkspaceUser[] {
    const all = this.removedPeople(this.users());
    return this.matchedUserIds ? all.filter((u) => this.matchedUserIds!.has(u.id)) : all;
  }

  /** Copies `user-mgmt.component.html`'s `initials()` treatment: first two characters of whatever
   *  name we have, falling back through name → email → username → "?". */
  initials(u: WorkspaceUser): string {
    return (u.userName || u.email || u.loginUsername || '?').trim().slice(0, 2).toUpperCase();
  }

  displayName(u: WorkspaceUser): string {
    return u.userName || u.email || u.loginUsername || '—';
  }

  // ─── Presentation only — colour, pictures and labels. Every RULE still comes from workspaces.util.ts. ───

  /** The workspace's tile: a monogram on a colour keyed to its id, so a rename never repaints it. */
  tileFill(): string {
    return toneFor(this.workspace.id).fill;
  }
  tileText(): string {
    return monogram(this.workspace.name);
  }

  /** `seatLabel`, drawn — see `seatMeter` (workspaces.util.ts) for why pips, a bar, or nothing. */
  meter(): SeatMeter {
    return seatMeter(this.workspace);
  }

  avatarFill(u: WorkspaceUser): string {
    return toneFor(u.id).fill;
  }

  /** The role's name for a pill (`roleName`) — and whether it IS a name: the stored-id fallback keeps
   *  its CSS capitalisation (what the pill always showed), a real name is printed as its author wrote it. */
  roleLabel(roleId: string): string {
    return roleName(roleId, this.roleNames);
  }
  roleIsNamed(roleId: string): boolean {
    return !!this.roleNames?.has(roleId);
  }
  /** Whether this row may change `u`'s role, and why not — `roleChangeControl` decides. */
  roleControl(u: WorkspaceUser) {
    const me = this.ws.callerEmail();
    return roleChangeControl(this.workspace, this.access, !!me && (u.email || '').toLowerCase() === me);
  }
  roleChoices(u: WorkspaceUser): RoleOption[] {
    const me = this.ws.callerEmail();
    const isSelf = !!me && (u.email || '').toLowerCase() === me;
    return roleOptions(this.roleNames, u.roleId, {
      lastAdmin: isLastAdmin(u, this.users()),
      ownBuildersRow: this.workspace.isBuilders && isSelf,
    });
  }
  /** The picker's title: the reason any choice is disabled, else what the pill is. */
  pickerTitle(u: WorkspaceUser): string {
    const refused = this.roleChoices(u).find((o) => o.disabled);
    if (refused) return refused.reason;
    return this.hasNoAccess(u) ? 'No access — still holds a seat until they are removed.' : 'Role in this app — change it here';
  }
  roleValue(u: WorkspaceUser): string {
    return roleValue(u.roleId);
  }
  roleErrorFor(u: WorkspaceUser): string {
    const e = this.roleError();
    return e && e.id === u.id ? e.message : '';
  }

  async changeRole(u: WorkspaceUser, sel: HTMLSelectElement): Promise<void> {
    const current = this.roleValue(u);
    const next = sel.value;
    if (next === current || this.savingRole() || this.pendingAdmin() || !this.roleControl(u).enabled) { sel.value = current; return; }
    if (next === 'admin') { this.pendingAdmin.set({ user: u, sel }); return; }
    await this.applyRole(u, next, sel);
  }

  cancelAdmin(): void {
    const p = this.pendingAdmin();
    if (!p) return;
    p.sel.value = this.roleValue(p.user);
    this.pendingAdmin.set(null);
  }

  async confirmAdmin(): Promise<void> {
    const p = this.pendingAdmin();
    if (!p) return;
    this.pendingAdmin.set(null);
    await this.applyRole(p.user, 'admin', p.sel);
  }

  /**
   * The picker is a plain `<select>` read through `[selected]`, not `ngModel` — the lesson of the
   * switches above (fix-round-1 finding 2): a control the user has just changed keeps its DOM value,
   * and on a refusal the bound expression never changes, so Angular never writes it back. So on a
   * refusal this puts the element back itself. On success the people list reloads — a role change
   * moves no seat, so `ngOnChanges`'s membership check would never notice it.
   */
  // Was (2026-10-30): private async applyRole(u: WorkspaceUser, next: string, sel: HTMLSelectElement): Promise<void> {
  private async applyRole(u: WorkspaceUser, next: string, sel?: HTMLSelectElement): Promise<SaveOutcome> {
    const current = this.roleValue(u);
    this.savingRole.set(u.id);
    this.roleError.set(null);
    const a = await this.ws.setUserRole(this.workspace.id, u.id, next);
    this.savingRole.set('');
    if (!a.ok) {
      if (sel) sel.value = current;
      this.roleError.set({ id: u.id, message: a.error });
      return { outcome: 'failed', reason: a.error };
    }
    await this.loadUsers(true);
    if (this.historyFor() === u.id) await this.loadHistory(u);
    return { outcome: 'saved', saved: { workspaceId: this.workspace.id, personId: u.id, roleId: next } };
  }

  /** A card stays up while the builder reads it, and the row's own picker can start a change meanwhile. */
  private async applyRoleIfFree(u: WorkspaceUser, next: string): Promise<SaveOutcome> {
    if (this.savingRole()) return { outcome: 'failed', reason: `A role on this workspace is still being changed. ${UNCHANGED}` };
    return this.applyRole(u, next);
  }

  /** The same dot a role wears on the Roles tab — keyed to the role id, so it matches there. */
  roleDot(roleId: string): string {
    return toneFor(roleId).dot;
  }

  /** The colour an invitation's status label wears. The WORDS are `invitationRow(...).label`, always —
   *  this only picks the tint beside them, and follows the same full-workspace check that label does. */
  inviteTone(i: WorkspaceInvitation): string {
    // ws 9b-1 — an invitation that never reached anyone, or no longer works, reads as needing attention.
    if (i.status === 'pending' && (i.expired || i.emailOutcome === 'failed')) return 'uw-status-full';
    // Was (2026-10-07, ws 9b-1 final review): if (i.status === 'pending' && i.emailOutcome === 'held') return 'uw-status-quiet';
    if (i.status === 'pending' && (i.emailOutcome === 'held' || i.emailOutcome === 'unsent')) return 'uw-status-quiet';
    if (i.status === 'pending') return this.seatsAreFull(this.workspace) ? 'uw-status-full' : 'uw-status-wait';
    if (i.status === 'accepted') return 'uw-status-done';
    if (i.status === 'linked_elsewhere') return 'uw-status-elsewhere';
    return 'uw-status-quiet';
  }

  /** The seat cap's − / + buttons. They only ever move the same draft the number field edits, through
   *  the same `onSeatCapEdited()` — so they obey exactly the rules typing a number does, and nothing is
   *  saved until Save. Never below 1: the field itself refuses less (`canSaveSeatCap`). */
  nudgeSeatCap(delta: number): void {
    if (this.seatCapUnlimited || !seatCapControl(this.workspace, this.access).enabled || this.savingSeatCap()) return;
    const current = Number(this.seatCapDraft);
    const base = Number.isFinite(current) && current >= 1 ? current : Math.max(this.workspace.activeCount, 1);
    this.seatCapDraft = Math.max(1, Math.round(base) + delta);
    this.onSeatCapEdited();
  }

  /** Whether there is an unsaved seat-cap edit — Save only asks for attention when there is one. */
  hasSeatCapEdit(): boolean {
    return this.seatCapDirty;
  }

  /**
   * `roleIsNoAccess` (workspaces.util.ts) is the shared decision: a falsy `roleId` and `'inactive'`
   * are ONE state to the server, not two — `gateway.function.ts` denies BOTH with the same "Account
   * not activated: no role assigned yet". This panel is what a builder reads to decide who to trust,
   * so it must never render a role name the person does not actually have — that ruled out
   * defaulting a blank roleId to `'admin'` (the most privileged role there is) in the template,
   * which is what the first cut of this row did.
   */
  hasNoAccess(u: WorkspaceUser): boolean {
    return roleIsNoAccess(u.roleId);
  }

  /** Same decision, for an invitation's role line — fix-round-1 finding 2: a hand-written `{{
   *  inv.roleId || 'No role' }}` described the same empty-roleId state differently from the pill
   *  six lines above it. Both now defer to `roleIsNoAccess`. */
  inviteHasNoAccess(i: WorkspaceInvitation): boolean {
    return roleIsNoAccess(i.roleId);
  }

  /**
   * The same door decision `add-person.component.ts`'s own `doors()` reads (Task 10, fix-round-1
   * finding 1) — the settings strip's switch descriptions and this row's invitations empty state both
   * call this rather than `doorsInto` directly, so a project-wide switch off, or the builders'
   * workspace with Developer mode off, can never be described one way here and a different way six
   * lines below in the add-person form. All three read the exact same verdict.
   */
  doors(): { invite: Control; add: Control } {
    return doorsInto(this.workspace, this.switches, { developerModeOn: this.developerModeOn });
  }

  /**
   * Task 8's empty state named only "this workspace" — the only fact it had. Now reads `doorsInto`
   * (workspaces.util.ts, fix-round-1 finding 5) instead of re-deciding the same project-before-
   * workspace precedence a second time with its own copy — the two had already drifted (fix-round-1
   * finding 3) before being pointed at the same function fixed it. That also means an empty
   * builders'-workspace invitation list correctly blames Developer mode being off, not the switches,
   * when that's the actual reason (fix-round-1 finding 4). Empty string means nothing is shut — the
   * plain "no invitations yet" case.
   */
  invitesEmptyReason(): string {
    const reason = this.doors().invite.reason;
    return reason ? `${reason} There are none here.` : '';
  }

  toggleRemoved(): void {
    this.showRemoved.set(!this.showRemoved());
  }

  /** The Removed list's date chip: ERASED for an erased person (`erasedAt` — people-rules.ts COALESCEs
   *  `removed_at` into it, so a person removed in May and erased in October must not read "May"), their
   *  `removedAt` otherwise. `createdAt` can be null (legacy/migrated rows) so it is never used here.
   *  Formatting is `shortDate` (workspaces.util.ts), shared with `auditLine` and, from Task 8 on,
   *  invitation dates — not a reimplementation per component. */
  removedDate(u: WorkspaceUser): string {
    return shortDate(this.isErased(u) ? u.erasedAt ?? null : u.removedAt);
  }

  /** No `stopPropagation` here: the Remove button lives inside the expanded panel, which is a
   *  SIBLING of the header that owns `onToggle` (not a descendant of it) — a click here has no path
   *  to bubble into the toggle, so there was nothing to stop. */
  openRemove(u: WorkspaceUser): void {
    this.removeError.set('');
    this.removeTarget.set(u);
  }

  cancelRemove(): void {
    if (this.removing()) return;
    this.removeTarget.set(null);
    this.removeError.set('');
    this.heard('remove-person', { outcome: 'discarded' });
  }

  openSetPassword(u: WorkspaceUser): void {
    this.pwTarget.set(u);
    this.pwValue.set(generatePassword());
    this.pwError.set('');
    this.pwDone.set(false);
    this.pwCopied.set(false);
  }

  regeneratePassword(): void {
    if (this.pwSaving() || this.pwDone()) return;
    this.pwValue.set(generatePassword());
    this.pwCopied.set(false);
    this.pwError.set('');
  }

  async copySetPassword(): Promise<void> {
    try { await navigator.clipboard.writeText(this.pwValue()); this.pwCopied.set(true); }
    catch { /* the password is on screen in plain text — nothing is lost */ }
  }

  closeSetPassword(): void {
    if (this.pwSaving()) return;
    this.pwTarget.set(null);
    this.pwValue.set('');
    this.pwDone.set(false);
    this.heard('set-person-password', { outcome: 'discarded' });
  }

  /** On failure the server's own message stays in the modal (e.g. the Test login / builder refusal). */
  async confirmSetPassword(): Promise<void> {
    const t = this.pwTarget();
    if (!t || this.pwSaving()) return;
    this.pwSaving.set(true);
    this.pwError.set('');
    this.heardSaving('set-person-password');
    const a = await this.ws.setPassword(this.workspace.id, t.id, this.pwValue());
    this.pwSaving.set(false);
    if (!a.ok) { this.pwError.set(a.error); this.heard('set-person-password', { outcome: 'failed', reason: withoutPassword(a.error, this.pwValue()) }); return; }
    this.pwDone.set(true);
    this.heard('set-person-password', { outcome: 'saved', saved: { workspaceId: this.workspace.id, personId: t.id } });
  }

  /**
   * On success, emit `changed` and stop — the tab's silent reload is what brings a fresh `workspace`
   * object back down, which `ngOnChanges` turns into this row's own re-fetch (see the comment there).
   * On failure, the server's own message is shown INSIDE the modal and the modal stays open: the two
   * refusals a builder actually hits ("That person is not in this workspace." and the last-admin
   * refusal) are things they need to read, not a toast that vanished before they finished the sentence.
   * W6 (ws 9a) adds three more, shown the same way: yourself, another builder unless you own the
   * project, and a test login.
   */
  async confirmRemove(): Promise<void> {
    const target = this.removeTarget();
    if (!target || this.removing()) return;
    this.removing.set(true);
    this.removeError.set('');
    this.heardSaving('remove-person');
    // Was (2026-10-06, ws 9a W6): const a = await this.ws.removeUser(target.id);
    const a = await this.ws.removeUser(this.workspace.id, target.id);
    this.removing.set(false);
    if (!a.ok) { this.removeError.set(a.error); this.heard('remove-person', { outcome: 'failed', reason: a.error }); return; }
    this.removeTarget.set(null);
    this.heard('remove-person', { outcome: 'saved', saved: { workspaceId: this.workspace.id, personId: target.id } });
    this.changed.emit();
  }

  onToggle(): void {
    this.toggle.emit();
  }

  /**
   * `app-add-person`'s `(added)` handler (Task 9, Step 5).
   *
   *  - `email` moves no seat at all: `activeCount` is unchanged, so `ngOnChanges`'s own
   *    `membershipChanged` branch (below) stays false and would never notice the new pending
   *    invitation on its own. `loadInvitations` is the one direct call this arm needs (`loadUsers`
   *    would be pure waste — nobody new is in the people list).
   *  - `username` DOES move a seat immediately, which is why fix-round-1 (finding 6) dropped this
   *    arm's own direct calls in favour of `changed.emit()` alone: the tab's `reload()` hands back a
   *    `Workspace` with a different `activeCount`, and `membershipChanged` already reloads BOTH lists
   *    for exactly that reason — a second, direct call here just ran each request twice on the
   *    common path.
   *
   *    fix-round-2 (finding 4) put them back anyway: relying solely on the tab's `reload()` means
   *    this row goes stale if THAT call fails for an unrelated reason — `WorkspacesTabComponent.
   *    reload()` sets its own `error` and returns without replacing `workspace`, so `ngOnChanges`
   *    never fires here at all, and the person who WAS just created successfully never appears in
   *    this row (the tab does show its own error, so the failure isn't silent — just misplaced).
   *    Paying for a duplicate `listUsers`/`listInvitations` call on the overwhelmingly common
   *    success path is cheaper than leaving a real success invisible on a failure elsewhere, so this
   *    arm calls both directly AND still emits `changed`.
   */
  onAdded(mode: AddPersonMode): void {
    if (mode === 'email') {
      this.loadInvitations(true);
    } else {
      this.loadUsers(true);
      this.loadInvitations(true);
    }
    this.changed.emit();
  }

  /** Any edit to the number field or the checkbox marks the draft dirty, so `ngOnChanges`'s resync
   *  (above) leaves it alone until this save resolves. */
  onSeatCapEdited(): void {
    this.seatCapDirty = true;
    this.seatCapError.set('');
  }

  /**
   * Gates the Save button as well as the two fields: disabled while the control itself is disabled
   * (owner-only / the builders' workspace — `seatCapControl`), while a save is already in flight, and
   * while an unchecked "Unlimited" box is paired with no usable number — sending `null` in that state
   * would silently uncap the workspace instead of setting the value the builder meant to type.
   */
  canSaveSeatCap(): boolean {
    if (!seatCapControl(this.workspace, this.access).enabled || this.savingSeatCap()) return false;
    if (this.seatCapUnlimited) return true;
    return this.seatCapDraft !== null && this.seatCapDraft !== undefined && Number(this.seatCapDraft) >= 1;
  }

  /**
   * The builders' workspace cap is disabled here on purpose (see `seatCapControl`). The server
   * refuses it as well; this is so a builder reads the rule instead of meeting it as a 409.
   *
   * Neither this nor `flipSwitch` gets fresh state back — only `list` does — so both emit `changed`
   * on success and let the tab's `reload()` bring a fresh `Workspace` down; nothing else re-fetches.
   */
  async saveSeatCap(): Promise<void> {
    if (!this.canSaveSeatCap()) return;
    const limit = this.seatCapUnlimited ? null : this.seatCapDraft;
    this.savingSeatCap.set(true);
    this.seatCapError.set('');
    this.seatCapOvershoot.set(null);
    this.heardSaving('set-seat-cap');
    const a = await this.ws.setSeatLimit(this.workspace.id, limit);
    this.savingSeatCap.set(false);
    if (!a.ok) { this.seatCapError.set(a.error); this.heard('set-seat-cap', { outcome: 'failed', reason: a.error }); return; }
    this.seatCapDirty = false;
    // Lowering a cap never removes anybody — a workspace can land over its new limit, and this is
    // the one place that says so, from THIS call's own response rather than waiting on a reload.
    // `a.data.limit !== null` is always true whenever `overshoot > 0` (the server's own `seatOvershoot`
    // returns 0 for an uncapped workspace — see `workspace-rules.ts`) but TypeScript can't see that
    // through the two fields, so this narrows explicitly rather than asserting it.
    if (a.data.limit !== null && a.data.overshoot > 0) {
      this.seatCapOvershoot.set({ activeCount: a.data.activeCount, limit: a.data.limit });
    }
    this.heard('set-seat-cap', { outcome: 'saved', saved: { workspaceId: this.workspace.id, seatLimit: a.data.limit, ...(a.data.overshoot > 0 ? { overshoot: a.data.overshoot } : {}) } });
    this.changed.emit();
  }

  /** Whether THIS control is mid-save — drives the "Saving…" label without disabling the other one. */
  // Was (2026-10-07, ws 9b-2): isSavingSwitch(which: 'invites' | 'adds'): boolean {
  isSavingSwitch(which: 'invites' | 'adds' | 'arrivals'): boolean {
    return this.savingSwitch() === which;
  }

  /**
   * `on` MEANS OPEN, on both switches — but UNLIKE `setProjectSwitch`, `setWorkspaceSwitch` does NOT
   * invert it (fix-round-1 finding 4: an earlier version of this comment claimed it did). The stored
   * columns here, `invites_enabled`/`adds_enabled`, are positive — `manage-workspaces.function.ts`'s
   * `setWorkspaceSwitch` case writes `body.on` straight through. Only the PROJECT-wide switches
   * (`signups_stopped`/`invites_disabled`) are stored inverted and flipped back by the server once.
   * The conclusion is the same either way — send `on` as "open" and never re-derive it from the
   * stored sense — but the reasoning matters if this ever needs to change.
   *
   * The control itself is a button reading the CURRENT `workspace.invitesEnabled`/`addsEnabled`
   * (fix-round-1 finding 2), not a checkbox with two-way state of its own: on failure this returns
   * having changed nothing, `workspace` is untouched, and the button already reads correctly next
   * render — there is no DOM state to fall out of step with the model, unlike a `[checked]` input the
   * user has just clicked, which Angular has no reason to re-write when the bound expression hasn't
   * changed.
   */
  // Was (2026-10-07, ws 9b-2): async flipSwitch(which: 'invites' | 'adds', on: boolean): Promise<void> {
  async flipSwitch(which: 'invites' | 'adds' | 'arrivals', on: boolean): Promise<SaveOutcome> {
    if (this.savingSwitch()) return { outcome: 'failed', reason: 'Another switch on this workspace is still saving.' };
    const shut = deletionReadOnly(this.workspace);
    if (shut) return { outcome: 'failed', reason: shut.reason };
    this.savingSwitch.set(which);
    this.switchError.set('');
    const a = await this.ws.setWorkspaceSwitch(this.workspace.id, which, on);
    this.savingSwitch.set('');
    if (!a.ok) { this.switchError.set(a.error); return { outcome: 'failed', reason: a.error }; }
    this.changed.emit();
    return { outcome: 'saved', saved: { workspaceId: this.workspace.id, [which]: on } };
  }

  /** The words and the gate for Claude asking to flip one of this row's switches. Only the card's button runs it. */
  prepareSwitch(which: 'invites' | 'adds', enabled: unknown): Prepared {
    if (typeof enabled !== 'boolean') return { refused: 'The switch needs enabled to be true or false. Nothing was changed.' };
    const shut = deletionReadOnly(this.workspace);
    if (shut) return { refused: `${shut.reason} Nothing was changed.` };
    const current = which === 'invites' ? this.workspace.invitesEnabled : this.workspace.addsEnabled;
    if (current === enabled) return { answer: { outcome: 'done', changed: [], result: { outcome: 'unchanged', workspaceId: this.workspace.id, [which]: enabled } } };
    const name = this.workspace.name;
    const says = which === 'invites'
      ? (enabled ? `Allow invitations into ${name}. ${ALLOW_INVITATIONS_COPY}` : `Stop invitations into ${name}. Nobody new can be invited by email until this is back on.`)
      : (enabled ? `Allow direct adds into ${name}. ${ALLOW_DIRECT_ADDS_COPY}` : `Stop direct adds into ${name}. Nobody can be given a username and password here until this is back on.`);
    return { says, confirmLabel: enabled ? 'Turn on' : 'Turn off', run: () => this.flipSwitch(which, enabled) };
  }

  private async inviteOnRow(inviteId: unknown, action: 'resend' | 'cancel'): Promise<WorkspaceInvitation | { refused: string }> {
    await this.loadInvitations(true);
    if (this.invitesError()) return { refused: `The invitations didn't load: ${this.invitesError()} Nothing was changed.` };
    const invite = this.invitations().find((i) => i.id === inviteId);
    if (!invite) return { refused: `${this.workspace.name} has no invitation with that id; read_screen lists them under invitations. Nothing was changed.` };
    if (!this.invitationRow(invite, { seatsFull: this.seatsAreFull(this.workspace) }).actions.includes(action)) {
      return { refused: `The invitation to ${invite.email} can't be ${action === 'resend' ? 'resent' : 'cancelled'} now. Nothing was changed.` };
    }
    return invite;
  }

  async prepareResend(inviteId: unknown): Promise<Prepared> {
    const invite = await this.inviteOnRow(inviteId, 'resend');
    if ('refused' in invite) return invite;
    const door = this.resendControl();
    if (!door.enabled) return { refused: `${door.reason} Nothing was changed.` };
    return { says: `Send the invitation to ${invite.email} again, to join ${this.workspace.name}. They get another email.`, confirmLabel: 'Resend', run: () => this.resendInvite(invite) };
  }

  async prepareCancel(inviteId: unknown): Promise<Prepared> {
    const shut = deletionReadOnly(this.workspace);
    if (shut) return { refused: `${shut.reason} Nothing was changed.` };
    const invite = await this.inviteOnRow(inviteId, 'cancel');
    if ('refused' in invite) return invite;
    return { says: `Cancel the invitation to ${invite.email} to join ${this.workspace.name}. Their link stops working.`, confirmLabel: 'Cancel invitation', run: () => this.cancelInvite(invite) };
  }

  /** The builders' workspace has no rename affordance in the template at all (`renameControl`), so
   *  this can only be reached for it if that ever changes. Refusing here too means the control and
   *  the action can't drift apart, the same way `canSaveSeatCap` re-checks `seatCapControl` rather
   *  than trusting the button's own `[disabled]`. */
  startEditName(ev: Event): void {
    ev.stopPropagation();
    if (!renameControl(this.workspace).enabled) return;
    this.nameDraft = this.workspace.name;
    this.renameError.set('');
    this.editingName.set(true);
    this.focusPending = true;
  }

  /** Escape cancels outright — no save, whatever was typed is discarded. */
  cancelEditName(): void {
    this.editingName.set(false);
    this.renameError.set('');
  }

  /**
   * Enter and blur both land here. The `renaming()` half of the guard matters: setting `renaming`
   * true disables the (still focused) input via `[disabled]`, and Chrome fires a synthetic `blur`
   * on an element the instant it is disabled — so an Enter-triggered save re-enters this method
   * via `(blur)` while the first call's request is still in flight. Without bailing on `renaming()`
   * that second call passes every other check (the name still differs from the stale
   * `workspace.name`) and posts the rename twice, which was firing two overlapping `reload()`s.
   */
  async saveEditName(): Promise<void> {
    if (!this.editingName() || this.renaming()) return;
    const name = this.nameDraft.trim();
    // Refuse silently and stay in edit mode — same as the create card does with an empty name —
    // rather than treating a cleared field as "cancel" and discarding the edit.
    if (!name) return;
    if (name === this.workspace.name) {
      this.cancelEditName();
      return;
    }
    // No length check here: `maxlength="60"` on the input already caps input, and unlike
    // `createWorkspace`, the server's `renameWorkspace` does not refuse an over-length name — it
    // silently truncates to 60. Reusing createWorkspace's "too long" refusal message here would
    // describe a rule this endpoint doesn't enforce.
    this.renaming.set(true);
    const a = await this.ws.renameWorkspace(this.workspace.id, name);
    this.renaming.set(false);
    if (!a.ok) {
      this.renameError.set(a.error);
      return;
    }
    this.editingName.set(false);
    this.changed.emit();
  }
  // ─── The same forms, for Claude: fast ones run the row's own method, the rest fill the form for the builder's press. ───

  /** The action Claude filled this row's form for. Only then does the button carry the mark, and only then does a
   *  press report: a builder's Save on a row Claude isn't waiting on says nothing to Claude's wait. */
  pressing = signal<string | null>(null);

  stopMarking(): void {
    this.pressing.set(null);
    this.addForm?.pressing.set(false);
  }

  private heardSaving(id: string): void {
    if (this.pressing() === id) this.waits.saving(id);
  }

  /** A failed save leaves the form up for another press, so only the other outcomes end the mark. */
  private heard(id: string, outcome: SaveOutcome): void {
    if (this.pressing() !== id) return;
    if (outcome.outcome !== 'failed') this.pressing.set(null);
    this.waits.report(id, outcome);
  }

  ngOnDestroy(): void {
    const id = this.pressing();
    if (id) this.heard(id, { outcome: 'closed', because: 'the workspace row left the screen' });
  }

  /** The id read_screen gives, or the name, email or username the builder knows them by. A search hiding them hides them here too. */
  personNamed(wanted: unknown): WorkspaceUser | { refused: string } {
    if (!this.usersLoaded) return { refused: `The people in ${this.workspace.name} didn't load: ${this.usersError() || 'try again in a moment.'} ${UNCHANGED}` };
    const shown = [...this.visibleActivePeople(), ...this.visibleRemovedPeople()];
    const byId = shown.find((u) => u.id === wanted);
    if (byId) return byId;
    const name = String(wanted ?? '').trim().toLowerCase();
    const named = shown.filter((u) => name && [u.userName, u.email, u.loginUsername].some((v) => (v || '').trim().toLowerCase() === name));
    if (named.length > 1) return { refused: `More than one person in ${this.workspace.name} goes by ${String(wanted)}; use their id from read_screen. ${UNCHANGED}` };
    return named[0] ?? { refused: `Nobody on show in ${this.workspace.name} goes by ${String(wanted)}. ${UNCHANGED}` };
  }

  /** Who the row has loaded, for read_screen. Nothing secret is on a person. */
  peopleHeld(): Record<string, unknown>[] {
    if (!this.usersLoaded) return [];
    return this.users().map((u) => ({
      workspaceId: this.workspace.id, id: u.id, name: u.userName, email: u.email, username: u.loginUsername, roleId: u.roleId,
      removed: !!u.removedAt, erased: this.isErased(u), canSetPassword: canSetPassword(u),
    }));
  }

  /** What a form on this row holds, by the action table's field names. A password goes out only as whether it is there. */
  formHeld(): Record<string, unknown> {
    const on = { workspaceId: this.workspace.id };
    const form = this.addForm;
    if (form?.open()) {
      return { ...on, email: form.email, username: form.username, roleId: form.roleId, password: form.password ? 'set' : '', generatedPassword: form.password ? 'set' : '' };
    }
    const pw = this.pwTarget();
    if (pw) return { ...on, personId: pw.id, password: this.pwValue() ? 'set' : '', generatedPassword: this.pwValue() ? 'set' : '' };
    const detail = this.detailTarget();
    if (detail) return { ...on, personId: detail.id, [this.detailMode()]: this.detailDraft };
    const removing = this.removeTarget();
    if (removing) return { ...on, personId: removing.id };
    if (this.editingName()) return { ...on, name: this.nameDraft };
    if (this.seatCapDirty) return { ...on, seatCap: this.seatCapDraft, unlimited: this.seatCapUnlimited };
    return {};
  }

  unsavedHere(): { count: number; name: string } | null {
    const called = this.workspace.name;
    if (this.editingName() && this.nameDraft.trim() !== called) return { count: 1, name: `The new name for ${called}` };
    if (this.seatCapHeld()) return { count: 1, name: `The seat cap of ${called}` };
    if (this.addForm?.holdsTyping()) return { count: 1, name: `The add form in ${called}` };
    if (this.pwTarget() && !this.pwDone()) return { count: 1, name: `The password form in ${called}` };
    if (this.removeTarget()) return { count: 1, name: `The remove form in ${called}` };
    if (this.transferFrom()) return { count: 1, name: `The transfer form in ${called}` };
    // Names the form only: the reinstate overlay's request carries a generated password.
    const asked = this.peopleAsked();
    if (asked) return { count: 1, name: `The ${asked} form in ${called}` };
    if (this.detailTarget() && !this.detailDone() && this.detailDraft.trim() !== (this.detailMode() === 'name' ? this.detailTarget()!.userName : '')) return { count: 1, name: `The form open in ${called}` };
    return null;
  }

  /** A draft edited back to what is saved is nothing to lose, and the field has no way to cancel it. */
  private seatCapHeld(): boolean {
    const draft = this.seatCapUnlimited ? null : Number(this.seatCapDraft);
    return this.seatCapDirty && draft !== (this.workspace.seatLimit ?? null);
  }

  private formUp(): boolean {
    return !!(this.pwTarget() || this.removeTarget() || this.detailTarget() || this.pendingAdmin() || this.transferFrom());
  }
  private static readonly FORM_UP = `The builder has a form open on this workspace; ask them to finish or cancel it first. ${UNCHANGED}`;

  private shutReason(): string {
    const shut = deletionReadOnly(this.workspace);
    return shut ? `${shut.reason} ${UNCHANGED}` : '';
  }

  async renameFor(wanted: unknown): Promise<ScreenActionAnswer> {
    const gate = renameControl(this.workspace);
    if (!gate.enabled) return { refused: `${gate.reason} ${UNCHANGED}` };
    if (this.editingName() || this.renaming()) return { refused: `The builder is renaming this workspace; ask them to finish first. ${UNCHANGED}` };
    const name = String(wanted ?? '').trim();
    if (!name) return { refused: `Enter a name for the workspace. ${UNCHANGED}` };
    // The input's maxlength is what holds a builder to this, and the server would cut the name short without a word.
    if (name.length > 60) return { refused: `A workspace name holds 60 characters at most. ${UNCHANGED}` };
    const id = this.workspace.id;
    if (name === this.workspace.name) return { done: `${name} already has that name.`, result: { outcome: 'unchanged', workspace: { id, name } } };
    const was = this.workspace.name;
    this.renameError.set('');
    this.nameDraft = name;
    this.editingName.set(true);
    await this.saveEditName();
    if (this.editingName()) {
      const said = this.renameError();
      this.cancelEditName();
      return { refused: `${said} ${UNCHANGED}` };
    }
    return { done: `Renamed ${was} to ${name}.`, result: { workspace: { id, name } } };
  }

  async personNameFor(u: WorkspaceUser, wanted: unknown): Promise<ScreenActionAnswer> {
    const shut = this.shutReason();
    if (shut) return { refused: shut };
    if (!canEditDetails(this.access)) return { refused: `Only the project's owner or an editor can change a person's name. ${UNCHANGED}` };
    if (u.removedAt) return { refused: `${this.displayName(u)} has been removed, so their name can't be changed. ${UNCHANGED}` };
    if (this.formUp()) return { refused: WorkspaceRowComponent.FORM_UP };
    const name = String(wanted ?? '').trim();
    const bad = nameDraftError(name);
    if (bad) return { refused: `${bad} ${UNCHANGED}` };
    const said = { workspaceId: this.workspace.id, personId: u.id, name };
    if (name === u.userName) return { done: `${name} already has that name.`, result: { outcome: 'unchanged', ...said } };
    this.openEditName(u);
    this.detailDraft = name;
    await this.saveDetail();
    if (this.detailTarget()) {
      const reason = this.detailError();
      this.closeDetail();
      return { refused: `${reason} ${UNCHANGED}` };
    }
    await this.loadUsers(true);
    return { done: `Changed ${this.displayName(u)}'s name to ${name}.`, result: said };
  }

  async arrivalsFor(enabled: unknown): Promise<ScreenActionAnswer> {
    if (typeof enabled !== 'boolean') return { refused: `The switch needs enabled to be true or false. ${UNCHANGED}` };
    const id = this.workspace.id;
    if (arrivalNoticeOn(this.workspace) === enabled) return { done: `Arrival notices were already ${enabled ? 'on' : 'off'}.`, result: { outcome: 'unchanged', workspaceId: id, arrivals: enabled } };
    const out = await this.flipSwitch('arrivals', enabled);
    if (out.outcome !== 'saved') return { refused: `${out.outcome === 'failed' ? out.reason : ''} ${UNCHANGED}` };
    return { done: `Turned arrival notices ${enabled ? 'on' : 'off'} for ${this.workspace.name}.`, result: out.saved };
  }

  prefillSeatCap(values: Record<string, unknown>): Prefilled {
    const gate = seatCapControl(this.workspace, this.access);
    if (!gate.enabled) return { refused: `${gate.reason} ${UNCHANGED}` };
    if (this.savingSeatCap()) return { refused: `This seat cap is still saving. ${UNCHANGED}` };
    if (this.seatCapHeld()) return { refused: `The builder has changed this seat cap without saving; ask them to save it first. ${UNCHANGED}` };
    const unlimited = values['unlimited'] === true;
    const cap = values['seatCap'];
    if (unlimited && cap !== undefined) return { refused: `A seat cap is a number or unlimited, not both. ${UNCHANGED}` };
    if (!unlimited && !(typeof cap === 'number' && Number.isInteger(cap) && cap >= 1)) return { refused: `A seat cap is a whole number of at least 1, or unlimited. ${UNCHANGED}` };
    const limit = unlimited ? null : cap as number;
    const id = this.workspace.id;
    if (limit === (this.workspace.seatLimit ?? null)) return { answer: { outcome: 'done', changed: [], result: { outcome: 'unchanged', workspaceId: id, seatLimit: limit } } };
    this.seatCapUnlimited = unlimited;
    this.seatCapDraft = limit;
    this.onSeatCapEdited();
    this.pressing.set('set-seat-cap');
    return {};
  }

  async prefillAdd(values: Record<string, unknown>): Promise<Prefilled> {
    const shut = this.shutReason();
    if (shut) return { refused: shut };
    if (!this.addForm) return { refused: `The add form isn't on the builder's screen. ${UNCHANGED}` };
    return this.addForm.prefill(values);
  }

  prefillRestore(): Prefilled {
    if (!this.canRestore()) return { refused: `${this.workspace.name} can't be restored: it isn't waiting to be deleted, or this account can't restore it. ${UNCHANGED}` };
    if (this.restoring()) return { refused: `${this.workspace.name} is being restored. ${UNCHANGED}` };
    this.restoreError.set('');
    this.confirmingRestore.set(true);
    this.pressing.set('restore-workspace');
    return {};
  }

  /** The role by id or by the name on the picker. The picker's own rules decide what can be chosen. */
  prepareRole(u: WorkspaceUser, wanted: unknown): Prepared {
    const gate = this.roleControl(u);
    if (!gate.enabled) return { refused: `${gate.reason} ${UNCHANGED}` };
    if (u.removedAt) return { refused: `${this.displayName(u)} has been removed; reinstating them sets their role. ${UNCHANGED}` };
    if (this.savingRole() || this.pendingAdmin()) return { refused: `A role on this workspace is still being changed. ${UNCHANGED}` };
    const text = String(wanted ?? '').trim();
    const choices = this.roleChoices(u);
    const choice = choices.find((o) => o.id === text) ?? choices.find((o) => o.label.toLowerCase() === text.toLowerCase());
    if (!choice) return { refused: `There is no role ${text} to give them; read_screen lists the roles. ${UNCHANGED}` };
    if (choice.id === this.roleValue(u)) return { answer: { outcome: 'done', changed: [], result: { outcome: 'unchanged', workspaceId: this.workspace.id, personId: u.id, roleId: choice.id } } };
    if (choice.disabled) return { refused: `${choice.reason} ${UNCHANGED}` };
    const who = this.displayName(u);
    if (choice.id === 'admin') {
      return { title: ADMIN_GRANT_TITLE, says: adminGrantBody(who, this.workspace.name), confirmLabel: 'Make admin', run: () => this.applyRoleIfFree(u, 'admin') };
    }
    return { says: `Change ${who}'s role in ${this.workspace.name} to ${choice.label}.`, confirmLabel: 'Change role', run: () => this.applyRoleIfFree(u, choice.id) };
  }

  /** Without a password this is the screen's own generated one, which stays on screen and is never in an answer. */
  prefillPassword(u: WorkspaceUser, given: unknown): Prefilled {
    const shut = this.shutReason();
    if (shut) return { refused: shut };
    if (!canSetPassword(u)) return { refused: `A password can't be set for ${this.displayName(u)}. ${UNCHANGED}` };
    if (this.formUp()) return { refused: WorkspaceRowComponent.FORM_UP };
    if (given !== undefined && (typeof given !== 'string' || given.length < 6)) return { refused: `A password holds at least 6 characters. ${UNCHANGED}` };
    this.openSetPassword(u);
    if (given !== undefined) this.pwValue.set(given as string);
    this.pressing.set('set-person-password');
    return { also: { passwordFrom: given === undefined ? 'generated' : 'given' } };
  }

  prefillEmail(u: WorkspaceUser, wanted: unknown): Prefilled {
    const shut = this.shutReason();
    if (shut) return { refused: shut };
    if (u.removedAt) return { refused: `${this.displayName(u)} has been removed. ${UNCHANGED}` };
    if (!canAddEmail(u, this.access)) {
      return { refused: canEditDetails(this.access)
        ? `${this.displayName(u)} already has an email, and this screen only adds one for someone with none. ${UNCHANGED}`
        : `Only the project's owner or an editor can add a person's email. ${UNCHANGED}` };
    }
    if (this.formUp()) return { refused: WorkspaceRowComponent.FORM_UP };
    const email = String(wanted ?? '').trim();
    const bad = emailDraftError(email);
    if (bad) return { refused: `${bad} ${UNCHANGED}` };
    this.openAddEmail(u);
    this.detailDraft = email;
    this.pressing.set('set-person-sign-in-email');
    return {};
  }

  prefillRemove(u: WorkspaceUser): Prefilled {
    const shut = this.shutReason();
    if (shut) return { refused: shut };
    if (!this.actions(u).remove) return { refused: `${this.displayName(u)} has already been removed. ${UNCHANGED}` };
    if (this.formUp()) return { refused: WorkspaceRowComponent.FORM_UP };
    this.openRemove(u);
    this.pressing.set('remove-person');
    return {};
  }

  /** Reads the summary and the preview, which change nothing. The people overlay's own button is what commits. */
  async prefillTransfer(u: WorkspaceUser, toWanted: unknown, entitiesWanted: unknown): Promise<Prefilled> {
    const blocked = this.peopleOverlayBlocked();
    if (blocked) return { refused: blocked };
    const to = this.personNamed(toWanted);
    if ('refused' in to) return to;
    if (to.removedAt || to.id === u.id) return { refused: `Work can only go to someone else who is still in ${this.workspace.name}. ${UNCHANGED}` };
    const s = await this.summaryFor(u);
    if (!s) return { refused: `${this.actionError()?.text} ${UNCHANGED}` };
    const choices = entityChoices(s.targets);
    if (entitiesWanted !== undefined && !(Array.isArray(entitiesWanted) && entitiesWanted.length)) return { refused: `entityIds lists the entities to transfer; leave it out to transfer them all. ${UNCHANGED}` };
    const wanted = (entitiesWanted as unknown[] | undefined)?.map((e) => String(e)) ?? choices.map((c) => c.id);
    const picked = wanted.map((w) => choices.find((c) => c.id === w || c.name.toLowerCase() === w.toLowerCase())?.id);
    const missing = wanted.filter((_, i) => !picked[i]);
    if (missing.length) return { refused: `${this.displayName(u)} has no work in ${missing.join(', ')}. ${UNCHANGED}` };
    if (!picked.length) return { refused: `${this.displayName(u)} has no work to transfer. ${UNCHANGED}` };
    const ids = picked as string[];
    const p = await this.people.previewTransfer(u.id, to.id, ids);
    if (!p.ok || !p.data.from || !p.data.to) return { refused: `${p.ok ? 'One of those people is not in this app.' : p.error} ${UNCHANGED}` };
    this.pressing.set('transfer-person');
    void this.askTransfer(u.id, to.id, ids, p.data);
    return {};
  }

  /** The words the screen asks to be typed are filled in, and the overlay's Erase is still the builder's to press. */
  async prefillErase(u: WorkspaceUser, confirmText: unknown): Promise<Prefilled> {
    const blocked = this.peopleOverlayBlocked();
    if (blocked) return { refused: blocked };
    if (!this.actions(u).erase) return { refused: `${this.displayName(u)} has already been erased. ${UNCHANGED}` };
    const s = await this.summaryFor(u);
    if (!s) return { refused: `${this.actionError()?.text} ${UNCHANGED}` };
    this.pressing.set('erase-person');
    void this.askErase(u, s, typeof confirmText === 'string' ? confirmText : undefined);
    return {};
  }

  async prefillReinstate(u: WorkspaceUser, roleWanted: unknown, given: unknown): Promise<Prefilled> {
    const blocked = this.peopleOverlayBlocked();
    if (blocked) return { refused: blocked };
    if (!this.actions(u).reinstate) return { refused: `${this.displayName(u)} can't be reinstated: they haven't been removed, or they have been erased. ${UNCHANGED}` };
    if (given !== undefined && (typeof given !== 'string' || given.length < 6)) return { refused: `A password holds at least 6 characters. ${UNCHANGED}` };
    const s = await this.summaryFor(u);
    if (!s) return { refused: `${this.actionError()?.text} ${UNCHANGED}` };
    const person = s.person!;
    if (person.signsInByLink && given !== undefined) return { refused: `${this.displayName(u)} signs in with an email link, so they take no password. ${UNCHANGED}` };
    const choices = this.reinstateChoices(person);
    const text = String(roleWanted ?? '').trim();
    const role = text ? choices.find((c) => c.id === text) ?? choices.find((c) => c.label.toLowerCase() === text.toLowerCase()) : undefined;
    if (text && !role) return { refused: `${this.displayName(u)} can't come back as ${text}; read_screen lists the roles. ${UNCHANGED}` };
    const password = person.signsInByLink ? '' : (given as string | undefined) ?? generatePassword();
    this.pressing.set('reinstate-person');
    void this.askReinstate(u, s, choices, role?.id, password);
    return person.signsInByLink ? {} : { also: { passwordFrom: given === undefined ? 'generated' : 'given' } };
  }

  /** A second request would cancel whatever the builder has up in the one people overlay. */
  private peopleOverlayBlocked(): string {
    const shut = this.shutReason();
    if (shut) return shut;
    if (this.peopleConfirm.busy() || this.peopleConfirm.current()) return `The builder has a confirmation open; ask them to finish or cancel it first. ${UNCHANGED}`;
    return this.formUp() ? WorkspaceRowComponent.FORM_UP : '';
  }
}
